/**
 * LiveLogsModal.jsx - Real-time log viewer for dev mode bots
 * ==========================================================
 * Version: 4.0.0 - State lifted to parent for persistence
 *
 * Features:
 * - VM controls (Start VM, Stop VM, Restart VM)
 * - Bot controls (Login Bot, Logout Bot)
 * - Category checkboxes for filtering
 * - Streaming controlled by parent (persists when modal closed)
 * - Light/dark mode support
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import {
    X, Trash2, Pause, Play, Download, Search, AlertCircle, Info, AlertTriangle,
    Power, PowerOff, RotateCcw, Phone, Mic, Volume2, Clock, Zap, FileText,
    CheckSquare, Square, Activity, LogIn, LogOut, Radio, Server
} from 'lucide-react'
import { monitoringApi } from '../services/api'
import './LiveLogsModal.css'

// Log level configuration
const LOG_LEVEL_CONFIG = {
    log: { color: '#e5e5e5', icon: null, label: 'LOG' },
    info: { color: '#3b82f6', icon: Info, label: 'INFO' },
    warn: { color: '#f59e0b', icon: AlertTriangle, label: 'WARN' },
    error: { color: '#ef4444', icon: AlertCircle, label: 'ERROR' }
}

// Log category configuration - exported for use by parent
export const LOG_CATEGORIES = {
    call: {
        label: 'Call',
        icon: Phone,
        color: '#22c55e',
        keywords: ['[Call]', 'call_start', 'call_end', 'disposition', 'hangup', 'DISPOSITION', 'Hanging up', 'Call started', 'Call ended']
    },
    transcriber: {
        label: 'Transcriber',
        icon: Mic,
        color: '#8b5cf6',
        keywords: ['[Transcriber]', '[VOSK]', '[Whisper]', 'transcription', 'speech', 'recognized']
    },
    audio: {
        label: 'Audio',
        icon: Volume2,
        color: '#06b6d4',
        keywords: ['[Audio]', 'playing', 'audio', 'TTS', 'voice', 'sound']
    },
    timing: {
        label: 'Timing',
        icon: Clock,
        color: '#f97316',
        keywords: ['[Timing]', '[PERF]', 'ms', 'took', 'duration', 'elapsed', 'latency']
    },
    action: {
        label: 'Actions',
        icon: Zap,
        color: '#eab308',
        keywords: ['[Action]', 'clicked', 'click', 'button', 'transfer', 'xfer', 'step']
    },
    heartbeat: {
        label: 'Heartbeat',
        icon: Activity,
        color: '#64748b',
        keywords: ['[Heartbeat]', '[HB]', 'Checking with server', 'Next check in', 'MAC address', 'key_string', 'Cache saved', 'Bot mode:', 'Key file written', 'Config file written', 'Activated with SIP', 'Starting client.js']
    },
    error: {
        label: 'Errors',
        icon: AlertCircle,
        color: '#ef4444',
        keywords: []
    },
    warn: {
        label: 'Warnings',
        icon: AlertTriangle,
        color: '#f59e0b',
        keywords: []
    },
    generic: {
        label: 'Generic',
        icon: FileText,
        color: '#6b7280',
        keywords: []
    }
}

// Categorize a log message - exported for use by parent
export function categorizeLog(log) {
    if (log.level === 'error') return 'error'
    if (log.level === 'warn') return 'warn'
    if (log.category && LOG_CATEGORIES[log.category]) return log.category

    const msg = log.message.toLowerCase()
    for (const [cat, config] of Object.entries(LOG_CATEGORIES)) {
        if (cat === 'error' || cat === 'warn' || cat === 'generic') continue
        for (const kw of config.keywords) {
            if (msg.includes(kw.toLowerCase())) return cat
        }
    }
    return 'generic'
}

export default function LiveLogsModal({
    bot,
    logs = [],
    isStreaming = false,
    connected = false,
    onToggleStreaming,
    onClearLogs,
    onAddLog,
    onClose
}) {
    const [paused, setPaused] = useState(false)
    const [filter, setFilter] = useState('')
    const [autoScroll, setAutoScroll] = useState(true)
    const [error, setError] = useState(null)
    const [commandLoading, setCommandLoading] = useState(null)

    // Category filters (all enabled by default except heartbeat)
    const [categoryFilters, setCategoryFilters] = useState({
        call: true,
        transcriber: true,
        audio: true,
        timing: true,
        action: true,
        heartbeat: false,  // Off by default - too noisy (every 30s)
        error: true,
        warn: true,
        generic: true
    })

    const logsEndRef = useRef(null)
    const logsContainerRef = useRef(null)

    // Auto-scroll to bottom when new logs arrive
    useEffect(() => {
        if (autoScroll && !paused && logsEndRef.current) {
            logsEndRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }, [logs, autoScroll, paused])

    // Handle scroll to detect if user scrolled up
    const handleScroll = useCallback(() => {
        if (!logsContainerRef.current) return
        const { scrollTop, scrollHeight, clientHeight } = logsContainerRef.current
        const isAtBottom = scrollHeight - scrollTop - clientHeight < 50
        setAutoScroll(isAtBottom)
    }, [])

    // Filter logs by category and search
    const filteredLogs = logs.filter(log => {
        const cat = log.category || 'generic'
        if (!categoryFilters[cat]) return false
        if (filter && !log.message.toLowerCase().includes(filter.toLowerCase())) return false
        return true
    })

    // Download logs
    const downloadLogs = () => {
        const content = logs.map(l =>
            `[${l.timestamp}] [${(l.category || 'generic').toUpperCase()}] [${l.level.toUpperCase()}] ${l.message}`
        ).join('\n')
        const blob = new Blob([content], { type: 'text/plain' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${bot.sip_id}-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`
        a.click()
        URL.revokeObjectURL(url)
    }

    // Format timestamp with milliseconds
    const formatTime = (timestamp) => {
        try {
            const date = new Date(timestamp)
            return date.toLocaleTimeString('en-US', {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                fractionalSecondDigits: 3
            })
        } catch (e) {
            return timestamp
        }
    }

    // Toggle category filter
    const toggleCategory = (cat) => {
        setCategoryFilters(prev => ({ ...prev, [cat]: !prev[cat] }))
    }

    const selectAllCategories = () => {
        setCategoryFilters(Object.keys(LOG_CATEGORIES).reduce((acc, cat) => ({ ...acc, [cat]: true }), {}))
    }

    const selectNoCategories = () => {
        setCategoryFilters(Object.keys(LOG_CATEGORIES).reduce((acc, cat) => ({ ...acc, [cat]: false }), {}))
    }

    // VM Controls
    const vmAction = async (action) => {
        if (!bot?.server_ip || !bot?.vmid) {
            setError('No VM linked to this bot')
            return
        }

        setCommandLoading(`vm_${action}`)
        try {
            if (action === 'start') {
                await monitoringApi.vmStart(bot.server_ip, bot.vmid, bot.sip_id, bot.dialer_id)
            } else if (action === 'stop') {
                await monitoringApi.vmStop(bot.server_ip, bot.vmid, bot.sip_id, bot.dialer_id)
            } else if (action === 'restart') {
                await monitoringApi.vmRestart(bot.server_ip, bot.vmid, bot.sip_id, false, bot.dialer_id)
            }
            onAddLog?.({
                timestamp: new Date().toISOString(),
                level: 'info',
                category: 'action',
                message: `[Dashboard] Sent VM ${action.toUpperCase()} command`
            })
        } catch (err) {
            setError(`Failed to ${action} VM: ${err.message}`)
        } finally {
            setCommandLoading(null)
        }
    }

    // Bot Controls (Login/Logout)
    const botCommand = async (command) => {
        if (!bot?.sip_id) return

        setCommandLoading(command)
        try {
            await monitoringApi.sendCommand([bot.sip_id], command, bot.dialer_id)
            onAddLog?.({
                timestamp: new Date().toISOString(),
                level: 'info',
                category: 'action',
                message: `[Dashboard] Sent ${command === 'start' ? 'LOGIN' : 'LOGOUT'} command to bot`
            })
        } catch (err) {
            setError(`Failed to send ${command} command: ${err.message}`)
        } finally {
            setCommandLoading(null)
        }
    }

    // Get category counts
    const categoryCounts = logs.reduce((acc, log) => {
        const cat = log.category || 'generic'
        acc[cat] = (acc[cat] || 0) + 1
        return acc
    }, {})

    const hasVM = bot?.server_ip && bot?.vmid

    return (
        <div className="ll-modal-overlay" onClick={onClose}>
            <div className="ll-modal ll-modal-wide" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="ll-header">
                    <div className="ll-title">
                        <span className="ll-title-icon">
                            {connected && isStreaming ? (
                                <span className="ll-status-dot ll-status-connected" />
                            ) : (
                                <span className="ll-status-dot ll-status-disconnected" />
                            )}
                        </span>
                        <span>Live Logs: {bot?.sip_id}</span>
                        <span className="ll-badge ll-badge-dev">DEV MODE</span>
                    </div>

                    {/* VM Controls */}
                    <div className="ll-control-group">
                        <span className="ll-control-label"><Server size={12} /> VM</span>
                        <button
                            className="ll-control-btn ll-control-start"
                            onClick={() => vmAction('start')}
                            disabled={commandLoading || !hasVM}
                            title={hasVM ? "Start VM" : "No VM linked"}
                        >
                            <Power size={14} />
                            {commandLoading === 'vm_start' ? '...' : 'Start'}
                        </button>
                        <button
                            className="ll-control-btn ll-control-stop"
                            onClick={() => vmAction('stop')}
                            disabled={commandLoading || !hasVM}
                            title={hasVM ? "Stop VM" : "No VM linked"}
                        >
                            <PowerOff size={14} />
                            {commandLoading === 'vm_stop' ? '...' : 'Stop'}
                        </button>
                        <button
                            className="ll-control-btn ll-control-restart"
                            onClick={() => vmAction('restart')}
                            disabled={commandLoading || !hasVM}
                            title={hasVM ? "Restart VM" : "No VM linked"}
                        >
                            <RotateCcw size={14} />
                            {commandLoading === 'vm_restart' ? '...' : 'Restart'}
                        </button>
                    </div>

                    {/* Bot Controls */}
                    <div className="ll-control-group">
                        <span className="ll-control-label"><Radio size={12} /> Bot</span>
                        <button
                            className="ll-control-btn ll-control-login"
                            onClick={() => botCommand('start')}
                            disabled={commandLoading}
                            title="Login Bot"
                        >
                            <LogIn size={14} />
                            {commandLoading === 'start' ? '...' : 'Login'}
                        </button>
                        <button
                            className="ll-control-btn ll-control-logout"
                            onClick={() => botCommand('stop')}
                            disabled={commandLoading}
                            title="Logout Bot"
                        >
                            <LogOut size={14} />
                            {commandLoading === 'stop' ? '...' : 'Logout'}
                        </button>
                    </div>

                    <button className="ll-close-btn" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                {/* Category Filters */}
                <div className="ll-categories">
                    <div className="ll-categories-header">
                        <span className="ll-categories-title">
                            <Activity size={14} />
                            Log Categories
                        </span>
                        <div className="ll-categories-actions">
                            <button onClick={selectAllCategories}>All</button>
                            <button onClick={selectNoCategories}>None</button>
                        </div>
                    </div>
                    <div className="ll-categories-list">
                        {Object.entries(LOG_CATEGORIES).map(([key, config]) => {
                            const Icon = config.icon
                            const count = categoryCounts[key] || 0
                            const isActive = categoryFilters[key]

                            return (
                                <button
                                    key={key}
                                    className={`ll-category-btn ${isActive ? 'll-category-active' : ''}`}
                                    onClick={() => toggleCategory(key)}
                                    style={{ '--cat-color': config.color }}
                                >
                                    {isActive ? <CheckSquare size={14} /> : <Square size={14} />}
                                    <Icon size={14} />
                                    <span>{config.label}</span>
                                    <span className="ll-category-count">{count}</span>
                                </button>
                            )
                        })}
                    </div>
                </div>

                {/* Toolbar */}
                <div className="ll-toolbar">
                    <div className="ll-toolbar-left">
                        <div className="ll-search">
                            <Search size={14} />
                            <input
                                type="text"
                                placeholder="Search logs..."
                                value={filter}
                                onChange={e => setFilter(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="ll-toolbar-right">
                        <span className="ll-log-count">
                            {filteredLogs.length} / {logs.length} logs
                        </span>
                        {/* Stream toggle */}
                        <button
                            className={`ll-tool-btn ${isStreaming ? 'll-tool-btn-streaming' : 'll-tool-btn-stopped'}`}
                            onClick={onToggleStreaming}
                            title={isStreaming ? 'Stop Live Streaming' : 'Start Live Streaming'}
                        >
                            <Radio size={14} />
                            <span>{isStreaming ? 'Streaming' : 'Stopped'}</span>
                        </button>
                        <button
                            className={`ll-tool-btn ${paused ? 'll-tool-btn-active' : ''}`}
                            onClick={() => setPaused(!paused)}
                            title={paused ? 'Resume' : 'Pause'}
                        >
                            {paused ? <Play size={14} /> : <Pause size={14} />}
                        </button>
                        <button className="ll-tool-btn" onClick={onClearLogs} title="Clear logs">
                            <Trash2 size={14} />
                        </button>
                        <button className="ll-tool-btn" onClick={downloadLogs} title="Save logs">
                            <Download size={14} />
                        </button>
                    </div>
                </div>

                {/* Error message */}
                {error && (
                    <div className="ll-error">
                        <AlertCircle size={14} />
                        <span>{error}</span>
                        <button onClick={() => setError(null)}><X size={12} /></button>
                    </div>
                )}

                {/* Logs container */}
                <div
                    className="ll-logs-container"
                    ref={logsContainerRef}
                    onScroll={handleScroll}
                >
                    {filteredLogs.length === 0 ? (
                        <div className="ll-empty">
                            {connected && isStreaming ? (
                                <>
                                    <div className="ll-empty-icon">Waiting for logs...</div>
                                    <div className="ll-empty-hint">
                                        Logs will appear here when the bot sends them.
                                        <br />
                                        Make sure the bot is running and in dev mode.
                                    </div>
                                </>
                            ) : !isStreaming ? (
                                <>
                                    <div className="ll-empty-icon">Streaming Stopped</div>
                                    <div className="ll-empty-hint">
                                        Click "Streaming" button to start receiving logs.
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="ll-empty-icon">Connecting...</div>
                                    <div className="ll-empty-hint">
                                        Establishing connection to log stream.
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        filteredLogs.map((log, idx) => {
                            const levelConfig = LOG_LEVEL_CONFIG[log.level] || LOG_LEVEL_CONFIG.log
                            const catConfig = LOG_CATEGORIES[log.category] || LOG_CATEGORIES.generic
                            const CatIcon = catConfig.icon
                            const LevelIcon = levelConfig.icon

                            return (
                                <div
                                    key={idx}
                                    className={`ll-log-line ll-log-${log.level} ll-cat-${log.category}`}
                                    style={{
                                        '--level-color': levelConfig.color,
                                        '--cat-color': catConfig.color
                                    }}
                                >
                                    <span className="ll-log-time">{formatTime(log.timestamp)}</span>
                                    <span className="ll-log-category" title={catConfig.label}>
                                        <CatIcon size={12} />
                                    </span>
                                    <span className="ll-log-level">
                                        {LevelIcon && <LevelIcon size={12} />}
                                        {levelConfig.label}
                                    </span>
                                    <span className="ll-log-message">{log.message}</span>
                                </div>
                            )
                        })
                    )}
                    <div ref={logsEndRef} />
                </div>

                {/* Footer */}
                <div className="ll-footer">
                    <span className={`ll-connection-status ${connected && isStreaming ? 'll-connected' : 'll-disconnected'}`}>
                        {connected && isStreaming ? 'Connected' : isStreaming ? 'Connecting...' : 'Disconnected'}
                    </span>
                    {paused && (
                        <span className="ll-paused-indicator">
                            <Pause size={12} /> Paused
                        </span>
                    )}
                    {!autoScroll && (
                        <button
                            className="ll-scroll-btn"
                            onClick={() => {
                                setAutoScroll(true)
                                logsEndRef.current?.scrollIntoView({ behavior: 'smooth' })
                            }}
                        >
                            Scroll to bottom
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}
